import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClipboardList,
  FaExclamationTriangle,
  FaPen,
  FaPlusCircle,
  FaSearch,
  FaUserGraduate,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { getCachedOrFetch, getTenantCacheKey, invalidateCache } from "../utils/requestCache";
import AdminModalShell from "./AdminModalShell";
import { useAppTheme } from "../context/AppThemeContext";
import AdminResponsiveTabs from "./AdminResponsiveTabs";
import ModuleImportButton from "./ModuleImportButton";
import ModuleRefreshButton from "./ModuleRefreshButton";

const dummyExams = [
  { id: "exam-1", title: "Unit Test 1", type: "INTERNAL", date: "2026-04-18", className: "Class 10", section: "A", subject: "Mathematics", duration: "90 mins", maxMarks: 100, invigilator: "Rahul Patil", status: "SCHEDULED" },
  { id: "exam-2", title: "Science Practical", type: "INTERNAL", date: "2026-04-21", className: "Class 9", section: "B", subject: "Science", duration: "60 mins", maxMarks: 50, invigilator: "Neha Kulkarni", status: "RESULT_PENDING" },
  { id: "exam-3", title: "English Midterm", type: "EXTERNAL", date: "2026-04-24", className: "Class 8", section: "C", subject: "English", duration: "75 mins", maxMarks: 80, invigilator: "Arjun Desai", status: "DRAFT" },
];

const emptyForm = { id: "", title: "", type: "INTERNAL", date: "", className: "", section: "", subject: "", duration: "", maxMarks: "", invigilator: "", status: "SCHEDULED" };

const normalizeExam = (record = {}, index = 0) => ({
  id: record.id || record.examId || record._id || `exam-${index + 1}`,
  title: record.title || record.examName || `Exam ${index + 1}`,
  type: String(record.type || record.examType || "INTERNAL").toUpperCase(),
  date: record.date || record.examDate || "",
  className: record.className || record.standard || "Class",
  section: record.section || "A",
  subject: record.subject || "Subject",
  duration: record.duration || "60 mins",
  maxMarks: Number(record.maxMarks ?? record.totalMarks ?? 100),
  invigilator: record.invigilator || record.teacherName || "Not assigned",
  status: String(record.status || "SCHEDULED").toUpperCase(),
});

const extractExams = (responseData) => {
  const payload = responseData?.data ?? responseData ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.exams)) return payload.exams;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

const pillTone = (status, isDarkMode) => {
  if (status === "SCHEDULED") return isDarkMode ? "border-sky-900/70 bg-sky-950/40 text-sky-200" : "border-sky-200 bg-sky-50 text-sky-700";
  if (status === "RESULT_PENDING") return isDarkMode ? "border-amber-900/70 bg-amber-950/40 text-amber-200" : "border-amber-200 bg-amber-50 text-amber-700";
  if (status === "DRAFT") return isDarkMode ? "border-slate-800 bg-slate-900 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600";
  return isDarkMode ? "border-emerald-900/70 bg-emerald-950/40 text-emerald-200" : "border-emerald-200 bg-emerald-50 text-emerald-700";
};

const AdminExamManagement = () => {
  const { isDarkMode } = useAppTheme();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dataSource, setDataSource] = useState("api");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedExamId, setSelectedExamId] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
    const [activeView, setActiveView] = useState("schedule");
  const [form, setForm] = useState(emptyForm);
  const [refreshVersion, setRefreshVersion] = useState(0);

  useEffect(() => {
    const fetchExams = async () => {
      setLoading(true);
      try {
        const response = await getCachedOrFetch(getTenantCacheKey("admin:exams"), () => api.get(API_PATHS.admin.exams()), { forceRefresh: refreshVersion > 0 });
        const nextExams = extractExams(response.data).map(normalizeExam);
        if (nextExams.length > 0) {
          setExams(nextExams);
          setDataSource("api");
          setSelectedExamId(nextExams[0].id);
        } else {
          setExams(dummyExams);
          setDataSource("dummy");
          setSelectedExamId(dummyExams[0].id);
        }
      } catch {
        setExams(dummyExams);
        setDataSource("dummy");
        setSelectedExamId(dummyExams[0].id);
      } finally {
        setLoading(false);
      }
    };
    fetchExams();
  }, [refreshVersion]);

  const selectedExam = useMemo(() => exams.find((exam) => exam.id === selectedExamId) || null, [selectedExamId, exams]);
  useEffect(() => { setForm(selectedExam || emptyForm); }, [selectedExam]);

  const filteredExams = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return exams.filter((exam) => !query || exam.title.toLowerCase().includes(query) || exam.subject.toLowerCase().includes(query) || exam.className.toLowerCase().includes(query) || exam.invigilator.toLowerCase().includes(query));
  }, [searchTerm, exams]);

  const summary = useMemo(() => ({ total: exams.length, scheduled: exams.filter((exam) => exam.status === "SCHEDULED").length, pending: exams.filter((exam) => exam.status === "RESULT_PENDING").length, drafts: exams.filter((exam) => exam.status === "DRAFT").length }), [exams]);
  const panelClass = isDarkMode ? "border-slate-800 bg-slate-900 shadow-black/20" : "border-slate-200 bg-white shadow-slate-200/60";

  const resetForm = () => { setSelectedExamId(""); setForm(emptyForm); };
  const openCreateForm = () => { resetForm(); setIsFormOpen(true); };
  const closeForm = () => { resetForm(); setIsFormOpen(false); };
  const openEditForm = (examId) => { setSelectedExamId(examId); setIsFormOpen(true); };
  const updateForm = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const upsertExam = (nextExam) => {
    setExams((current) => {
      const exists = current.some((exam) => exam.id === nextExam.id);
      if (!exists) return [nextExam, ...current];
      return current.map((exam) => (exam.id === nextExam.id ? nextExam : exam));
    });
    setSelectedExamId(nextExam.id);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    const payload = { name: form.title.trim(), title: form.title.trim(), type: form.type, date: form.date, className: form.className.trim(), section: form.section.trim(), subject: form.subject.trim(), duration: form.duration.trim(), maxMarks: Number(form.maxMarks || 0), invigilator: form.invigilator.trim(), status: form.status };
    const isEditing = Boolean(form.id);
    try {
      const response = isEditing ? await api.put(API_PATHS.admin.examById(form.id), payload) : await api.post(API_PATHS.admin.exams(), payload);
      const nextExam = normalizeExam({ ...(response.data?.data ?? response.data ?? {}), ...payload, id: isEditing ? form.id : undefined }, exams.length);
      upsertExam(nextExam);
      invalidateCache(getTenantCacheKey("admin:exams"));
      setDataSource("api");
      resetForm();
      setIsFormOpen(false);
      toast.success(isEditing ? "Exam updated." : "Exam created.");
    } catch (error) {
      toast.error(error?.message || "Exam could not be saved on the server.");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <AdminResponsiveTabs tabs={[{ key: "metrics", label: "Metrics" }, { key: "schedule", label: "Schedule" }]} activeKey={activeView} onChange={setActiveView} tone="indigo" isDarkMode={isDarkMode} />

      <div className={`border-x border-b p-6 shadow-lg ${panelClass}`}>
        {dataSource === "dummy" && (
          <div className={`mb-4 rounded-xl border px-4 py-3 text-sm ${isDarkMode ? "border-amber-900/70 bg-amber-950/40 text-amber-100" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
            <div className="flex items-start gap-3">
              <FaExclamationTriangle className="mt-0.5 shrink-0" />
              <p className="font-semibold">Demo data — API unavailable</p>
            </div>
          </div>
        )}

        {activeView === "metrics" && (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {[
                { label: "Total exams", value: summary.total },
                { label: "Scheduled", value: summary.scheduled },
                { label: "Result pending", value: summary.pending },
                { label: "Drafts", value: summary.drafts },
              ].map((item) => (
                <div key={item.label} className={`rounded-2xl border p-4 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
                  <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{item.label}</p>
                  <p className="mt-2 text-2xl font-bold">{item.value}</p>
                </div>
              ))}
            </div>
            <div className={`overflow-hidden rounded-2xl border ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className={isDarkMode ? "bg-slate-900 text-slate-300" : "bg-slate-100 text-slate-600"}>
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Metric</th>
                      <th className="px-4 py-3 text-left font-semibold">Value</th>
                    </tr>
                  </thead>
                  <tbody className={isDarkMode ? "bg-slate-950 text-slate-200" : "bg-white text-slate-800"}>
                    <tr className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}><td className="px-4 py-3">Total exams</td><td className="px-4 py-3">{summary.total}</td></tr>
                    <tr className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}><td className="px-4 py-3">Scheduled</td><td className="px-4 py-3">{summary.scheduled}</td></tr>
                    <tr className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}><td className="px-4 py-3">Result pending</td><td className="px-4 py-3">{summary.pending}</td></tr>
                    <tr className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}><td className="px-4 py-3">Drafts</td><td className="px-4 py-3">{summary.drafts}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeView === "schedule" && (
          <>
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="relative flex-1">
                <FaSearch className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className={`w-full rounded-2xl border py-2.5 pl-10 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500" : "border-slate-300 bg-white text-slate-900"}`}
                  placeholder="Search exams, class, subject, invigilator"
                />
              </div>
                <ModuleRefreshButton onRefresh={() => setRefreshVersion((value) => value + 1)} />
                <ModuleImportButton moduleKey="exams" label="Import exams" />
                <button
                type="button"
                onClick={openCreateForm}
                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-600 whitespace-nowrap"
              >
                <FaPlusCircle /> Create exam
              </button>
            </div>

            <div className={`overflow-hidden rounded-2xl border ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className={isDarkMode ? "bg-slate-900 text-slate-300" : "bg-slate-100 text-slate-600"}>
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Title</th>
                      <th className="px-4 py-3 text-left font-semibold">Class</th>
                      <th className="px-4 py-3 text-left font-semibold">Subject</th>
                      <th className="px-4 py-3 text-left font-semibold">Date</th>
                      <th className="px-4 py-3 text-left font-semibold">Duration</th>
                      <th className="px-4 py-3 text-left font-semibold">Invigilator</th>
                      <th className="px-4 py-3 text-left font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className={isDarkMode ? "bg-slate-950 text-slate-200" : "bg-white text-slate-800"}>
                    {loading ? (
                      <tr><td colSpan="7" className={`px-4 py-10 text-center ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Loading exam records...</td></tr>
                    ) : filteredExams.length === 0 ? (
                      <tr><td colSpan="7" className={`px-4 py-10 text-center ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No exams found.</td></tr>
                    ) : filteredExams.map((exam) => (
                      <tr
                        key={exam.id}
                        onClick={() => openEditForm(exam.id)}
                        className={`cursor-pointer border-t transition ${isDarkMode ? "border-slate-800 hover:bg-slate-800/60" : "border-slate-200 hover:bg-slate-50"} ${exam.id === selectedExamId ? isDarkMode ? "bg-indigo-950/20" : "bg-indigo-50" : ""}`}
                      >
                        <td className="px-4 py-3 font-semibold">{exam.title}</td>
                        <td className="px-4 py-3">{exam.className} {exam.section}</td>
                        <td className="px-4 py-3">{exam.subject}</td>
                        <td className="px-4 py-3">{exam.date}</td>
                        <td className="px-4 py-3">{exam.duration}</td>
                        <td className="px-4 py-3">{exam.invigilator}</td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${pillTone(exam.status, isDarkMode)}`}>
                            {exam.status.replaceAll("_", " ")}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      <AdminModalShell isOpen={isFormOpen} onClose={closeForm} isDarkMode={isDarkMode}>
        <div className={`rounded-3xl border p-6 shadow-lg ${panelClass}`}>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Exam form</p>
              <h2 className="mt-2 text-2xl font-bold">{form.id ? "Update exam" : "Create exam"}</h2>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Exam title</label>
                <input type="text" value={form.title} onChange={(event) => updateForm("title", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="Unit Test 1" required />
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Type</label>
                <select value={form.type} onChange={(event) => updateForm("type", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`}>
                  <option value="INTERNAL">Internal</option>
                  <option value="EXTERNAL">External</option>
                </select>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Date</label>
                <input type="date" value={form.date} onChange={(event) => updateForm("date", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} required />
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Duration</label>
                <input type="text" value={form.duration} onChange={(event) => updateForm("duration", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="90 mins" required />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Class</label>
                <input type="text" value={form.className} onChange={(event) => updateForm("className", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="Class 10" required />
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Section</label>
                <input type="text" value={form.section} onChange={(event) => updateForm("section", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="A" required />
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Subject</label>
                <input type="text" value={form.subject} onChange={(event) => updateForm("subject", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="Mathematics" required />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Invigilator</label>
                <input type="text" value={form.invigilator} onChange={(event) => updateForm("invigilator", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="Teacher name" required />
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Max marks</label>
                <input type="number" min="0" value={form.maxMarks} onChange={(event) => updateForm("maxMarks", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="100" required />
              </div>
            </div>
            <div>
              <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Status</label>
              <select value={form.status} onChange={(event) => updateForm("status", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`}>
                <option value="SCHEDULED">Scheduled</option>
                <option value="RESULT_PENDING">Result pending</option>
                <option value="DRAFT">Draft</option>
                <option value="COMPLETED">Completed</option>
              </select>
            </div>
            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={saving} className="inline-flex items-center rounded-full bg-indigo-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-600 disabled:opacity-70">{saving ? "Saving..." : form.id ? "Update exam" : "Create exam"}</button>
              <button type="button" onClick={() => setIsFormOpen(false)} className={`inline-flex items-center rounded-full border px-5 py-2.5 text-sm font-semibold transition ${isDarkMode ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>Cancel</button>
            </div>
          </form>
        </div>
      </AdminModalShell>
    </div>
  );
};

export default AdminExamManagement;
